<template>
  <div class="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
    <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
      <span>📊</span>
      <span>四维度分析雷达图</span>
    </h3>
    
    <div class="radar-chart-wrapper">
      <svg :width="svgSize" :height="svgSize" :viewBox="`0 0 ${svgSize} ${svgSize}`" class="radar-svg">
        <!-- 背景网格 -->
        <g v-for="level in 5" :key="'grid-' + level">
          <polygon
            :points="getPolygonPoints(level * 20)"
            fill="none"
            :stroke="level === 5 ? '#E5E7EB' : '#F3F4F6'"
            stroke-width="1"
          />
        </g>

        <!-- 坐标轴 -->
        <g v-for="(dimension, index) in dimensions" :key="'axis-' + index">
          <line
            :x1="center"
            :y1="center"
            :x2="getPoint(index, 100).x"
            :y2="getPoint(index, 100).y"
            stroke="#D1D5DB"
            stroke-width="1"
          />
        </g>

        <!-- 数据区域 -->
        <polygon
          :points="dataPolygonPoints"
          :fill="fillColor"
          :stroke="strokeColor"
          stroke-width="3"
          fill-opacity="0.3"
          class="transition-all duration-500"
        />

        <!-- 数据点 -->
        <g v-for="(dimension, index) in dimensions" :key="'point-' + index">
          <circle
            :cx="getPoint(index, scores[dimension.key]).x"
            :cy="getPoint(index, scores[dimension.key]).y"
            r="6"
            :fill="strokeColor"
            class="transition-all duration-500"
          />
        </g>

        <!-- 维度标签 -->
        <g v-for="(dimension, index) in dimensions" :key="'label-' + index">
          <text
            :x="getLabelPoint(index).x"
            :y="getLabelPoint(index).y"
            text-anchor="middle"
            dominant-baseline="middle"
            class="text-sm font-semibold fill-gray-700"
          >
            {{ dimension.name }}
          </text>
          <text
            :x="getLabelPoint(index).x"
            :y="getLabelPoint(index).y + 16"
            text-anchor="middle"
            dominant-baseline="middle"
            class="text-xs fill-gray-500"
          >
            {{ scores[dimension.key] }}分
          </text>
        </g>
      </svg>
    </div>

    <!-- 维度说明 -->
    <div class="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      <div 
        v-for="dimension in dimensions" 
        :key="dimension.key"
        class="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
      >
        <div class="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-psychology-primary to-psychology-secondary flex items-center justify-center text-white font-bold">
          {{ scores[dimension.key] }}
        </div>
        <div class="flex-1">
          <h4 class="font-semibold text-gray-900 text-sm">{{ dimension.name }}</h4>
          <p class="text-xs text-gray-600 mt-1">{{ dimension.description }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FourDimensionRadar',
  props: {
    scores: {
      type: Object,
      required: true,
      validator: (value) => {
        return value.MAT !== undefined && 
               value.CAR !== undefined && 
               value.GUI !== undefined && 
               value.IND !== undefined
      }
    },
    size: {
      type: Number,
      default: 400
    }
  },
  data() {
    return {
      dimensions: [
        { key: 'MAT', name: '成熟偏好', description: '对伴侣成熟度的偏好' },
        { key: 'CAR', name: '照顾需求', description: '被照顾的需求强度' },
        { key: 'GUI', name: '引导欲望', description: '引导照顾对方的欲望' },
        { key: 'IND', name: '独立倾向', description: '保持独立性的需求' }
      ],
      fillColor: 'rgba(255, 51, 102, 0.2)',
      strokeColor: '#FF3366',
      containerWidth: 400
    }
  },
  computed: {
    // 响应式 SVG 尺寸：移动端自适应，桌面端使用默认值
    svgSize() {
      // 在移动端，使用容器宽度减去 padding，但不超过 400px
      if (typeof window !== 'undefined') {
        const isMobile = window.innerWidth <= 768
        if (isMobile) {
          // 移动端：使用视口宽度减去 padding（左右各 16px + 卡片 padding 24px * 2）
          const maxWidth = Math.min(window.innerWidth - 80, 350)
          return Math.max(280, maxWidth)
        }
      }
      return this.size
    },
    center() {
      return this.svgSize / 2
    },
    radius() {
      // 根据 SVG 尺寸动态调整 radius，确保标签不会超出
      // 移动端减少标签距离，避免超出
      //const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
      //const labelOffset = isMobile ? 30 : 40
      return this.svgSize / 2 - 60
    },
    labelDistance() {
      // 移动端标签距离更近，避免超出
      const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
      return isMobile ? 30 : 40
    },
    dataPolygonPoints() {
      return this.dimensions
        .map((dim, index) => {
          const point = this.getPoint(index, this.scores[dim.key])
          return `${point.x},${point.y}`
        })
        .join(' ')
    }
  },
  methods: {
    getPoint(index, value) {
      const angle = (Math.PI * 2 * index) / this.dimensions.length - Math.PI / 2
      const distance = (value / 100) * this.radius
      return {
        x: this.center + Math.cos(angle) * distance,
        y: this.center + Math.sin(angle) * distance
      }
    },
    getLabelPoint(index) {
      const angle = (Math.PI * 2 * index) / this.dimensions.length - Math.PI / 2
      const distance = this.radius + this.labelDistance
      return {
        x: this.center + Math.cos(angle) * distance,
        y: this.center + Math.sin(angle) * distance
      }
    },
    getPolygonPoints(value) {
      return this.dimensions
        .map((_, index) => {
          const point = this.getPoint(index, value)
          return `${point.x},${point.y}`
        })
        .join(' ')
    },
    handleResize() {
      // 触发响应式更新
      this.$forceUpdate()
    }
  },
  mounted() {
    // 监听窗口大小变化，更新 SVG 尺寸
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleResize)
      this.handleResize()
    }
  },
  beforeUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize)
    }
  }
}
</script>

<style scoped>
.radar-chart-wrapper {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  padding: 0;
}

.radar-svg {
  width: 100%;
  height: auto;
  max-width: 400px;
  overflow: visible;
}

@media (max-width: 768px) {
  .radar-chart-wrapper {
    padding: 0 8px;
  }
  
  .radar-svg {
    max-width: 100%;
  }
}

@media (max-width: 480px) {
  .radar-chart-wrapper {
    padding: 0 4px;
  }
}
</style>
